import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Parse } from "../../lib/parse";
import { clearSession } from "../../lib/auth";
import { isAdminRole } from "../../config/navigation";
import { useBrandingConfig } from "../../hooks/useBrandingConfig";
import logoDanasignWhite from "../../assets/logo-danasign-white.png";
import Icon from "./Icon";

const parseStoredJson = (key) => {
  try {
    const rawValue = localStorage.getItem(key);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    return null;
  }
};

const resolveTenantName = () => {
  const extClass = parseStoredJson("Extand_Class");
  const extTenant = Array.isArray(extClass)
    ? extClass?.[0]?.TenantId?.TenantName || extClass?.[0]?.tenantName
    : null;
  if (typeof extTenant === "string" && extTenant.trim()) {
    return extTenant.trim();
  }

  const tenantName = localStorage.getItem("TenantName");
  if (typeof tenantName === "string" && tenantName.trim()) {
    return tenantName.trim();
  }

  const userDetails = parseStoredJson("userDetails");
  const detailsCompany = userDetails?.company || userDetails?.userDetails?.company;
  if (typeof detailsCompany === "string" && detailsCompany.trim()) {
    return detailsCompany.trim();
  }

  return "";
};

const formatRole = (rawRole) => {
  if (typeof rawRole !== "string" || !rawRole.trim()) {
    return "Usuario";
  }

  return rawRole
    .replace(/^contracts_/i, "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
};

const resolveProfileInfo = () => {
  const extClass = parseStoredJson("Extand_Class");
  const extUser = Array.isArray(extClass) ? extClass?.[0] || {} : {};
  const userInformation = parseStoredJson("UserInformation") || {};
  const userDetails = parseStoredJson("userDetails") || {};

  const fullNameFromParts = [extUser?.FirstName, extUser?.LastName]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ")
    .trim();

  const name =
    extUser?.Name ||
    fullNameFromParts ||
    userInformation?.name ||
    userDetails?.userDetails?.name ||
    userDetails?.name ||
    localStorage.getItem("username") ||
    userInformation?.username ||
    "";

  const email =
    localStorage.getItem("userEmail") ||
    extUser?.Email ||
    userInformation?.email ||
    userInformation?.username ||
    "-";

  const company =
    resolveTenantName() ||
    extUser?.Company ||
    userDetails?.company ||
    userDetails?.userDetails?.company ||
    "";

  const rawRole = extUser?.UserRole || userInformation?.UserRole || userDetails?.userDetails?.role || "";
  const role = formatRole(rawRole);

  return {
    name,
    email,
    company,
    role,
    rawRole
  };
};

function TopNav({
  showMobileMenuButton = false,
  isMobileSidebarOpen = false,
  onToggleMobileSidebar = () => {}
}) {
  const { t } = useTranslation();
  
  // Get tenantId from localStorage
  const getTenantId = () => {
    try {
      const extClassRaw = localStorage.getItem("Extand_Class");
      const extClass = extClassRaw ? JSON.parse(extClassRaw) : null;
      return Array.isArray(extClass) ? extClass?.[0]?.TenantId?.objectId : null;
    } catch (error) {
      return null;
    }
  };
  
  const tenantId = getTenantId();
  const { branding } = useBrandingConfig(tenantId);
  const defaultDocsUrl = "https://drive.google.com/file/d/1urDpE05Sm-J3hfO8gvlkk8JBoDx_TvEm/view?usp=drive_link";
  const docsUrl = branding?.documentationUrl || defaultDocsUrl;
  const navigate = useNavigate();
  const [profileInfo, setProfileInfo] = useState(() => resolveProfileInfo());
  const companyName = profileInfo.company || t("new-ui.common.tenant", { defaultValue: "Tenant" });
  const profileName = profileInfo.name || t("user", { defaultValue: "User" });
  const canAccessAdminConfiguration = isAdminRole(profileInfo.rawRole);

  useEffect(() => {
    const refreshProfileMeta = () => {
      setProfileInfo(resolveProfileInfo());
    };

    window.addEventListener("dana:tenant-brand-updated", refreshProfileMeta);
    return () => {
      window.removeEventListener("dana:tenant-brand-updated", refreshProfileMeta);
    };
  }, []);

  const onLogout = async () => {
    try {
      await Parse.User.logOut();
    } catch (error) {
      // Session may already be invalid.
      console.error("Logout warning", error);
    }

    clearSession();
    navigate("/", { replace: true });
  };

  const onHelp = () => {
    window.open(docsUrl, "_blank", "noopener,noreferrer");
  };

  const onProfileSettings = () => {
    navigate("/profile");
  };

  const onUserPreferences = () => {
    navigate("/profile/preferences");
  };

  const onChangePassword = () => {
    navigate("/changepassword");
  };

  const onEditSignature = () => {
    navigate("/managesign");
  };

  const onApiToken = () => {
    navigate("/generatetoken");
  };

  return (
    <header className="dana-topbar">
      <div className="dana-topbar-left">
        {showMobileMenuButton ? (
          <button
            type="button"
            className="dana-top-icon dana-mobile-menu-btn"
            aria-label={
              isMobileSidebarOpen
                ? t("new-ui.sidebar.close-navigation", { defaultValue: "Close navigation" })
                : t("new-ui.sidebar.open-navigation", { defaultValue: "Open navigation" })
            }
            title={
              isMobileSidebarOpen
                ? t("new-ui.sidebar.close-navigation", { defaultValue: "Close navigation" })
                : t("new-ui.sidebar.open-navigation", { defaultValue: "Open navigation" })
            }
            onClick={onToggleMobileSidebar}
          >
            <Icon name={isMobileSidebarOpen ? "x" : "menu"} />
          </button>
        ) : null}
        <img
          src={branding?.headerLogo || logoDanasignWhite}
          alt={branding?.appName || "DANAsign"}
          className="dana-brand-logo"
        />
        <div className="dana-brand-text">
          <div className="dana-brand-title">{companyName}</div>
        </div>
      </div>
      <div className="dana-topbar-right">
        <button
          type="button"
          className="dana-top-icon dana-top-help-btn"
          aria-label={t("new-ui.common.help", { defaultValue: "Help" })}
          title={t("new-ui.common.help", { defaultValue: "Help" })}
          onClick={onHelp}
        >
          <Icon name="help" />
        </button>
        <div className="dana-user-meta">
          <button
            type="button"
            className="dana-top-icon dana-user-chip"
            aria-label={t("profile", { defaultValue: "Profile" })}
            title={t("profile", { defaultValue: "Profile" })}
          >
            <Icon name="user" />
          </button>
          <span className="dana-user-name" title={profileName}>
            {profileName}
          </span>
          <div className="dana-profile-popover">
            <div className="dana-profile-head">
              <p className="dana-profile-title">{t("profile", { defaultValue: "Profile" })}</p>
              <p className="dana-profile-subtitle">{profileName}</p>
            </div>
            <div className="dana-profile-grid">
              <div className="dana-profile-row">
                <span>{t("email", { defaultValue: "Email" })}</span>
                <strong>{profileInfo.email}</strong>
              </div>
              <div className="dana-profile-row">
                <span>{t("new-ui.common.tenant", { defaultValue: "Tenant" })}</span>
                <strong>{profileInfo.company}</strong>
              </div>
              <div className="dana-profile-row">
                <span>{t("report-heading.Role", { defaultValue: "Role" })}</span>
                <strong>{profileInfo.role || t("report-heading.Role", { defaultValue: "Role" })}</strong>
              </div>
            </div>
            <div className="dana-profile-divider" />
            <div className="dana-profile-actions">
              <button type="button" className="dana-profile-action" onClick={onProfileSettings}>
                <span className="dana-profile-action-icon" aria-hidden="true">
                  <Icon name="user" />
                </span>
                <span>{t("new-ui.topnav.edit-profile", { defaultValue: "Edit profile" })}</span>
              </button>
              <button type="button" className="dana-profile-action" onClick={onUserPreferences}>
                <span className="dana-profile-action-icon" aria-hidden="true">
                  <Icon name="sliders" />
                </span>
                <span>{t("sidebar.Settings-Children.Preferences", { defaultValue: "Preferences" })}</span>
              </button>
              <button type="button" className="dana-profile-action" onClick={onChangePassword}>
                <span className="dana-profile-action-icon" aria-hidden="true">
                  <Icon name="key" />
                </span>
                <span>{t("change-password", { defaultValue: "Change password" })}</span>
              </button>
              <button type="button" className="dana-profile-action" onClick={onEditSignature}>
                <span className="dana-profile-action-icon" aria-hidden="true">
                  <Icon name="signature" />
                </span>
                <span>{t("new-ui.topnav.edit-signature", { defaultValue: "Edit signature" })}</span>
              </button>
              {canAccessAdminConfiguration ? (
                <button type="button" className="dana-profile-action" onClick={onApiToken}>
                  <span className="dana-profile-action-icon" aria-hidden="true">
                    <Icon name="key" />
                  </span>
                  <span>{t("api-token", { defaultValue: "API token" })}</span>
                </button>
              ) : null}
            </div>
            <div className="dana-profile-divider" />
            <button type="button" className="dana-profile-logout" onClick={onLogout}>
              {t("log-out", { defaultValue: "Log out" })}
            </button>
          </div>
        </div>
      </div>
      <div className="dana-topbar-line" />
    </header>
  );
}

export default TopNav;
